import {
	Card,
	CheckboxFormInput,
	useLanguageResource,
} from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { getEnv } from "../../../env";
import type { CompanyWithAvailableSubscriptions } from "../../../types/Company";
import "./Conditions.scss";
import conditionsLanguageResource from "./lang-resources";

const inputName = "conditions";

interface Props {
	showCompanyTicket: boolean;
	showSubscription: boolean;
	selectedCompany?: CompanyWithAvailableSubscriptions;
}

const Conditions: React.FC<Props> = ({
	showCompanyTicket,
	showSubscription,
	selectedCompany,
}) => {
	const lang = useLanguageResource(conditionsLanguageResource);
	const isHousingAgreement =
		selectedCompany?.subTypeId === "HOUSING_COOPERATIVE_AGREEMENT";

	const {
		formState: { errors },
		trigger,
	} = useFormContext();

	useEffect(() => {
		if (errors[inputName]?.message?.toString()) {
			void trigger();
		}
	}, [errors, trigger]);

	const renderRuterLabel = () => {
		const companyTicketTerms = (
			<a
				target="_blank"
				rel="noopener noreferrer"
				href="https://ruter.no/fa-hjelp/vilkar/personvern/avtale-ruter-profil/vilkar-bedriftsbillett/"
			>
				{lang.generalConditionsRuter}
			</a>
		);

		const ruterTerms = (
			<a
				target="_blank"
				rel="noopener noreferrer"
				href="https://ruter.no/fa-hjelp/vilkar/reisevilkar"
			>
				{lang.conditionsOfTravelRuter}
			</a>
		);

		const termsForEBike = (
			<a
				target="_blank"
				rel="noopener noreferrer"
				href="https://bedrift.ruter.no/vilkar/reiseabonnement"
			>
				Vilkår og personvernerklæring for reiseabonnement
			</a>
		);

		const getHousingAssociationTerms = isHousingAgreement
			? " Jeg bekrefter også at eier av leiligheten er gjort kjent med og godtar at kostnadene faktureres han eller henne."
			: "";

		const getTermsTextWithLinks = () => {
			if (showCompanyTicket && !showSubscription) {
				return (
					<>
						Jeg aksepterer
						{companyTicketTerms}
						og
						{ruterTerms}.{getHousingAssociationTerms}
					</>
				);
			}
			if (!showCompanyTicket && showSubscription) {
				return (
					<>
						Jeg aksepterer
						{termsForEBike}. {getHousingAssociationTerms}
					</>
				);
			}
			return (
				<>
					Jeg aksepterer
					{companyTicketTerms},{ruterTerms} og {termsForEBike}.{" "}
					{getHousingAssociationTerms}
				</>
			);
		};

		return getTermsTextWithLinks();
	};

	const renderAKTLabel = () => {
		return (
			<>
				Jeg aksepterer
				<a
					target="_blank"
					rel="noopener noreferrer"
					href="https://www.akt.no/kundeservice/rettigheter-og-regler/vilkar-for-bedriftsbillett-i-akt-billett-appen/"
				>
					{lang.generalConditionsAKT}
				</a>
			</>
		);
	};

	const renderLabel = () => {
		const env = getEnv();
		switch (env.tenant) {
			case "akt":
				return renderAKTLabel();
			case "ruter":
				return renderRuterLabel();
			default: {
				const exhaustiveCheck: never = env.tenant;
				throw new Error(`Unhandled switch case: ${exhaustiveCheck}`);
			}
		}
	};

	return (
		<Card
			className="components-input-conditions"
			data-test-id="components-input-conditions"
		>
			<Card.Content>
				<CheckboxFormInput
					name={inputName}
					variant="normal"
					validationLabel="Vilkår(ene)"
					label={renderLabel()}
					required
				></CheckboxFormInput>
			</Card.Content>
		</Card>
	);
};

export default Conditions;
